/* eslint-disable react/display-name */
import React, { useContext } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { GridInitialState, UnitsContext } from '@innovyze/stylovyze';
import { ScoreCell } from './ScoreCell';
import { RangeCell } from './RangeCell';
import { SkeletonCell } from './SkeletonCell';
import { useGlobalization } from '@Translations/useGlobalization';
import { useSelectIsCountLoading } from '@Selectors/Risk.selectors';
import { getGradeLabel } from '@Utils/gradeLabels';

type TranslateFn = ReturnType<typeof useGlobalization>['t'];

export const getWidth = (
	field: string,
	dataGridState?: GridInitialState,
): number | undefined => {
	// Get the width of the column from the saved state if available
	if (
		dataGridState &&
		dataGridState.columns?.dimensions &&
		dataGridState.columns.dimensions[field]
	) {
		return dataGridState.columns.dimensions[field].width;
	} else return undefined;
};

export const getFlex = (
	field: string,
	flex: number,
	dataGridState?: GridInitialState,
): number | undefined => {
	// If there is a saved width then return undefined
	// We dont want to specify both flex and width since flex will override the width
	if (
		dataGridState &&
		dataGridState.columns?.dimensions &&
		dataGridState.columns.dimensions[field] &&
		dataGridState.columns.dimensions[field].width
	) {
		return undefined;
	} else return flex;
};

const scoreColumn = (
	t: TranslateFn,
	onScoreChange: (index: number, value: number) => void,
	scoreMax?: number,
	hideCounts?: boolean,
	dataGridState?: GridInitialState,
): GridColDef => ({
	field: 'score',
	headerName: t('Score'),
	sortable: false,
	hideable: false,
	align: 'left',
	flex: hideCounts ? undefined : getFlex('score', 0.5, dataGridState),
	width: getWidth('score', dataGridState) ?? hideCounts ? 150 : undefined,
	minWidth: 100,
	renderCell: params => (
		<ScoreCell
			id={params.id}
			value={params.value}
			onScoreChange={onScoreChange}
			scoreMax={scoreMax}
		/>
	),
});

const uniqueValueColumn = (
	t: TranslateFn,
	hideCounts?: boolean,
	dataGridState?: GridInitialState,
	isDeteriorationModel?: boolean,
): GridColDef => ({
	field: 'value',
	headerName: t('Value'),
	sortable: false,
	hideable: false,
	align: 'left',
	flex: hideCounts ? undefined : getFlex('value', 1, dataGridState),
	width: getWidth('value', dataGridState) ?? hideCounts ? 300 : undefined,
	minWidth: 100,
	valueFormatter: isDeteriorationModel
		? params => getGradeLabel(params.value as string, t)
		: undefined,
});

interface RangeValueColumnOptions {
	onRangeChange: (index: number, value: number, valueUnit?: number) => void;
	isRiskCountLoading: boolean;
	hideCounts?: boolean;
	unit?: string;
	currency?: string;
	precision?: number;
	dataGridState?: GridInitialState;
}

const rangeValueColumn = (
	t: TranslateFn,
	{
		onRangeChange,
		isRiskCountLoading,
		hideCounts,
		unit,
		currency,
		precision,
		dataGridState,
	}: RangeValueColumnOptions,
): GridColDef => ({
	field: 'value',
	headerName: t('Value'),
	sortable: false,
	hideable: false,
	align: 'left',
	flex: hideCounts ? undefined : getFlex('value', 1.5, dataGridState),
	width: getWidth('value', dataGridState) ?? hideCounts ? 500 : undefined,
	minWidth: 200,
	renderCell: params => (
		<RangeCell
			id={params.id}
			value={params.value}
			onRangeChange={onRangeChange}
			disabled={isRiskCountLoading}
			unit={unit}
			currency={currency}
			precision={precision}
		/>
	),
});

const skeletonColumn = (
	field: string,
	headerName: string,
	countKey: string,
	dataGridState?: GridInitialState,
	type?: 'number',
): GridColDef => ({
	field,
	headerName,
	sortable: false,
	hideable: false,
	align: 'left',
	headerAlign: 'left',
	type,
	flex: getFlex(field, 1, dataGridState),
	width: getWidth(field, dataGridState),
	renderCell: params => (
		<SkeletonCell
			id={params.id}
			countKey={countKey}
			value={params.value}
			percent={false}
		/>
	),
});

const countColumns = (
	t: TranslateFn,
	countKey: string,
	lengthUnit: string,
	dataGridState?: GridInitialState,
): GridColDef[] => [
	skeletonColumn(
		'count',
		t('Asset Count'),
		countKey,
		dataGridState,
		'number',
	),
	skeletonColumn(
		'countPercent',
		t('Asset Count (%)'),
		countKey,
		dataGridState,
	),
	skeletonColumn(
		'length',
		t('Length ({{unit}})', { unit: lengthUnit }),
		countKey,
		dataGridState,
		'number',
	),
	skeletonColumn('lengthPercent', t('Length (%)'), countKey, dataGridState),
];

export const tableColumns = (
	forComponent: boolean,
	onScoreChange: (index: number, value: number) => void,
	onRangeChange: (index: number, value: number, valueUnit?: number) => void,
	isUnique: boolean,
	countKey: string,
	scoreMax?: number,
	unit?: string,
	currency?: string,
	precision?: number,
	dataGridState?: GridInitialState,
	hideCounts?: boolean,
	isDeteriorationModel?: boolean,
) => {
	const { t } = useGlobalization();
	const { system } = useContext(UnitsContext);
	const lengthUnit = system === 'Metric' ? t('Kilometers') : t('Miles');

	const isRiskCountLoading = useSelectIsCountLoading(countKey);

	const header = new Array<GridColDef>();

	if (t) {
		if (forComponent) {
			header.push(
				scoreColumn(
					t,
					onScoreChange,
					scoreMax,
					hideCounts,
					dataGridState,
				),
			);
		}

		if (isUnique) {
			header.push(
				uniqueValueColumn(
					t,
					hideCounts,
					dataGridState,
					isDeteriorationModel,
				),
			);
		} else {
			header.push(
				rangeValueColumn(t, {
					onRangeChange,
					isRiskCountLoading,
					hideCounts,
					unit,
					currency,
					precision,
					dataGridState,
				}),
			);
		}

		if (!hideCounts) {
			header.push(
				...countColumns(t, countKey, lengthUnit, dataGridState),
			);
		}
	}

	return header;
};
